import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";

import {
  IPD_COLORS,
  IPD_SHAPE,
  ipdElevatedInputSx,
  ipdLabelSx,
  IpdButton,
  IpdTextField,
} from "../../ipd/utils";
import { searchMedicinePrices } from "../api/emergencyBillingApi";
import { formatDateTime } from "../constants";
import { formatINR } from "../utils/charges";

const DEBOUNCE_MS = 300;

/** A batch that can be charged: the server priced it, and it has not expired. */
const usable = (batch) => Boolean(batch?.unit_price) && !batch?.expired;

/**
 * Saying what a dose was, so it can be charged: the pharmacy-catalogue item, the
 * batch, and how many issue units.
 *
 * The prices come from the server and are for choosing only. Posting prices the
 * batch again, so nothing shown or typed here changes what the patient is billed.
 * A batch with no issue-unit price cannot be chosen — the pharmacy counter refuses
 * the same batch, and a guessed rate is worse than no charge.
 */
export default function MedicinePriceDialog({ open, visitId, dose, onClose, onChoose }) {
  const [query, setQuery] = useState("");
  const [items, setItems] = useState([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState("");
  const [item, setItem] = useState(null);
  const [batchNumber, setBatchNumber] = useState("");
  const [quantity, setQuantity] = useState("1");
  const reqRef = useRef(0);

  useEffect(() => {
    if (!open) return;
    setQuery(dose?.medicine_name || "");
    setItems([]);
    setItem(null);
    setBatchNumber("");
    setQuantity("1");
    setError("");
  }, [open, dose]);

  useEffect(() => {
    if (!open) return undefined;
    const term = query.trim();
    if (term.length < 2) {
      setItems([]);
      return undefined;
    }
    const reqId = ++reqRef.current;
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const rows = await searchMedicinePrices(visitId, term);
        if (reqRef.current === reqId) {
          setItems(rows);
          setError("");
        }
      } catch (e) {
        if (reqRef.current === reqId) {
          setItems([]);
          setError(e?.message || "The pharmacy catalogue could not be searched");
        }
      } finally {
        if (reqRef.current === reqId) setSearching(false);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, open, visitId]);

  if (!dose) return null;

  const batches = item?.batches || [];
  const batch = batches.find((b) => b.batch_number === batchNumber) || null;
  const qty = Number(quantity);
  const qtyValid = Number.isInteger(qty) && qty > 0;
  const ready = Boolean(item && usable(batch) && qtyValid);
  const lineTotal = ready ? Number(batch.unit_price) * qty : null;
  const unitLabel = item?.issue_uom_code || "unit";

  const chooseItem = (next) => {
    setItem(next);
    // The server lists batches earliest-expiry first, so the first usable one is FEFO.
    setBatchNumber((next?.batches || []).find(usable)?.batch_number || "");
  };

  const confirm = () => {
    onChoose({
      itemId: item.item_id,
      itemName: item.item_name,
      batchNumber,
      quantity: qty,
      unitPrice: batch.unit_price,
      uom: item.issue_uom_code || "",
    });
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: `${IPD_SHAPE.radiusLg}px` } }}
    >
      <DialogTitle sx={{ pb: 0.5 }}>
        <Typography sx={{ fontSize: 17, fontWeight: 700, color: IPD_COLORS.textPrimary }}>
          What was given?
        </Typography>
        <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary, mt: 0.25 }}>
          {[dose.medicine_name, dose.dose, dose.route].filter(Boolean).join(" ")} · given{" "}
          {formatDateTime(dose.administered_at)}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2, display: "grid", gap: 2 }}>
        {error && (
          <Alert severity="error" sx={{ borderRadius: `${IPD_SHAPE.radiusMd}px` }}>
            {error}
          </Alert>
        )}

        <Box>
          <Typography sx={{ ...ipdLabelSx, mb: 0.75 }}>Pharmacy item</Typography>
          <Autocomplete
            size="small"
            options={items}
            loading={searching}
            value={item}
            inputValue={query}
            // Without this, MUI resets the input to the (empty) selection on open,
            // wiping the dose's medicine name the search starts from.
            clearOnBlur={false}
            filterOptions={(x) => x}
            getOptionLabel={(o) => o?.item_name || ""}
            isOptionEqualToValue={(a, b) => a?.item_id === b?.item_id}
            onInputChange={(_, v) => setQuery(v)}
            onChange={(_, v) => chooseItem(v)}
            noOptionsText={searching ? "Searching…" : "No pharmacy item matches"}
            renderOption={(props, o) => (
              <li {...props} key={o.item_id}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{o.item_name}</Typography>
                  <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>
                    {[o.generic_name, o.item_code, o.controlled ? "Controlled" : null]
                      .filter(Boolean)
                      .join(" · ")}
                    {(o.batches || []).some(usable) ? "" : " · no priced stock"}
                  </Typography>
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search the pharmacy catalogue…"
                sx={ipdElevatedInputSx}
                inputProps={{ ...params.inputProps, "aria-label": "Pharmacy item" }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {searching ? <CircularProgress size={15} sx={{ mr: 1 }} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Box>

        {item && batches.length === 0 && (
          <Alert severity="info" sx={{ borderRadius: `${IPD_SHAPE.radiusMd}px` }}>
            No stock of this item has been received into inventory, so it has no price yet.
          </Alert>
        )}
        {item && batches.length > 0 && !batches.some(usable) && (
          <Alert severity="info" sx={{ borderRadius: `${IPD_SHAPE.radiusMd}px` }}>
            None of this item's batches has a price per {unitLabel} that can be charged. The
            pharmacy counter refuses these batches too.
          </Alert>
        )}

        {item && batches.some(usable) && (
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 150px" }, gap: 1.5 }}>
            <Box>
              <Typography sx={{ ...ipdLabelSx, mb: 0.75 }}>Batch</Typography>
              <IpdTextField
                select
                value={batchNumber}
                onChange={(e) => setBatchNumber(e.target.value)}
                fullWidth
                sx={ipdElevatedInputSx}
                inputProps={{ "aria-label": "Batch" }}
              >
                {batches.map((b) => (
                  <MenuItem key={b.batch_number} value={b.batch_number} disabled={!usable(b)}>
                    {b.batch_number}
                    {b.expiry_date ? ` · exp ${b.expiry_date}` : ""}
                    {b.expired
                      ? " · expired"
                      : b.unit_price
                      ? ` · ${formatINR(b.unit_price)} per ${unitLabel}`
                      : " · no price"}
                  </MenuItem>
                ))}
              </IpdTextField>
            </Box>
            <Box>
              <Typography sx={{ ...ipdLabelSx, mb: 0.75 }}>Quantity ({unitLabel})</Typography>
              <IpdTextField
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                fullWidth
                sx={ipdElevatedInputSx}
                error={!qtyValid}
                helperText={qtyValid ? " " : "Whole units only"}
                inputProps={{ min: 1, step: 1, "aria-label": "Quantity" }}
              />
            </Box>
          </Box>
        )}

        <Typography sx={{ fontSize: 12, color: IPD_COLORS.textMuted }}>
          The price is the pharmacy's current sale price for the batch, looked up again when you
          post. Charging a medicine does not take it out of stock.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, justifyContent: "space-between" }}>
        <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
          {lineTotal !== null ? `Total ${formatINR(lineTotal)}` : ""}
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <IpdButton variant="outlined" onClick={onClose}>
            Cancel
          </IpdButton>
          <IpdButton onClick={confirm} disabled={!ready}>
            Use this
          </IpdButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
